/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { useMemo } from 'react'
import { BLOG_POST_MEDIA_PATH_S3 } from 'constants/images'
import { getTableOfContent, validateTableOfContent } from 'components/TableOfContent'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { useImageUpload } from 'utils/saveImageS3'
import { PageLoader } from 'components/PageLoader'
import { BlogPostType, Form, VALIDATE_TABLE_OF_CONTENT_OPTIONS } from 'pagesNew/SuccessStory/Create/Form'
import * as classes from 'pagesNew/SuccessStory/Create/styles'
import { FormValues } from 'pagesNew/SuccessStory/Create/types'
import { CREATE_STUDY_GUIDE } from 'queries/studyGuide'
import { createStudyGuide, createStudyGuideVariables } from '__generatedTypes__/createStudyGuide'
import { handleServerErrors, prepareHtmlForSubmit } from 'pagesNew/SuccessStory/Create'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { toast } from 'utils/toast'
import { useGetSimilarPostsInitialValue } from './hooks'

export const REFETCH_STUDY_GUIDES_MESSAGE = 'refetch-study-guides'

type Props = {
  platform: Platform
}

export const Create = withPlatformCheck(({ platform }: Props) => {
  const bc = useBroadcastChannel()

  const uploadImage = useImageUpload()

  const { loading, getInitialValue } = useGetSimilarPostsInitialValue(platform.id)

  const [createStudyGuide] = useMutation<createStudyGuide, createStudyGuideVariables>(CREATE_STUDY_GUIDE)

  const handleSubmit = async (values: FormValues) => {
    try {
      const { title, description = '', featuredArticleDescription, slug, withWhiteImageBG = false } = values

      const tableOfContent = getTableOfContent(values.contentAsHtml)
      const { error } = validateTableOfContent(tableOfContent, VALIDATE_TABLE_OF_CONTENT_OPTIONS)

      if (error) {
        return
      }

      const guideAsHtml = await prepareHtmlForSubmit(values)

      const coverImageFile = values.coverImage?.[0]?.file

      const coverImage = await (coverImageFile
        ? uploadImage(coverImageFile, BLOG_POST_MEDIA_PATH_S3)
        : Promise.resolve(''))

      const authorId = values.author[0].id || ''

      await createStudyGuide({
        variables: {
          createStudyGuideInput: {
            platform: {
              id: platform.id,
            },
            title,
            description,
            featuredArticleDescription,
            slug,
            author: {
              id: authorId,
            },
            guideAsHtml,
            withWhiteImageBG,
            coverImage,
            tableOfContent,
            similarGuides: values.similarPosts?.map(({ id }) => id),
            icon: values.icon?.id,
          },
        },
      })

      bc.postMessage(REFETCH_STUDY_GUIDES_MESSAGE)
      window.close()
    } catch (error: unknown) {
      toast.error('Failed to create study guide')

      return handleServerErrors(error)
    }
  }

  const initialValues = useMemo(
    () => ({
      similarPosts: getInitialValue([]),
    }),
    [loading]
  )

  if (loading) {
    return <PageLoader />
  }

  return (
    <section css={classes.container}>
      <Form
        initialValues={initialValues}
        blogPostType={BlogPostType.StudyGuide}
        platform={platform}
        onSubmit={handleSubmit}
      />
    </section>
  )
})
